(function(algolia) {
  'use strict';

  // Extract render and html functions from Algolia externals for template rendering
  const render = algolia.externals.render;
  const html = algolia.externals.html;

  // Initialize Algolia search client with app ID and API key
  var algoliasearch = window.algoliasearch.algoliasearch;
  if(typeof algoliasearch == "undefined") return;

  // Create search client - use window.search_api_key if available, otherwise fall back to config
  if(window.search_api_key) {
    algolia.searchClient = algoliasearch(
      algolia.config.app_id,
      window.search_api_key
    );
  }
  else {
    algolia.searchClient = algoliasearch(
      algolia.config.app_id,
      algolia.config.search_api_key
    );
  }

  /**
   * Renders an Algolia template into a container with provided data.
   *
   * @function render
   * @param {Function} template - The template function to render
   * @param {HTMLElement} container - DOM element to render into
   * @param {Object} data - Data to pass to the template
   * @returns {void}
   *
   * @example
   * algolia.render(myTemplate, document.querySelector('#results'), { hits: [...] });
   */
  algolia.render = (template, container, data) => {
    return render(template(html, data), container);
  };

  /**
   * Formats a price value for display using Shopify's money formatting.
   *
   * @function formatPrice
   * @param {number|string} value - The raw price value (in cents)
   * @param {boolean|string} [currency=false] - Currency code or false to use default
   * @returns {string} Formatted price string
   *
   * @example
   * formatPrice(1999, 'USD'); // Returns "$19.99"
   */
  var formatPrice = function formatPrice(value, currency = false) {
    return algolia.formatMoney(Number(value) * 100, currency);
  };

  /**
   * Escapes HTML special characters to prevent XSS attacks.
   *
   * @function escapeHtml
   * @param {string} unsafe - The string to escape
   * @returns {string} HTML-escaped string
   *
   * @example
   * escapeHtml('<script>alert("xss")</script>');
   * // Returns "&lt;script&gt;alert(&quot;xss&quot;)&lt;/script&gt;"
   */
  var escapeHtml = function escapeHtml(unsafe) {
    return (unsafe || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  };

  /**
   * Helper functions for Algolia search integration.
   * Provides utilities for price formatting, image handling, market support, and search operations.
   *
   * @namespace algolia.helpers
   *
   * @example
   * // Access helpers
   * algolia.helpers.formatNumber(1000); // Returns "1,000"
   */
  algolia.helpers = {
    /**
     * Cached product metafields data from session storage.
     * Used for quick access to product metadata during search rendering.
     *
     * @type {Object}
     */
    localData: sessionStorage.getItem('product_metafields') ? JSON.parse(sessionStorage.getItem('product_metafields')) : {},

    /**
     * Checks if multiple markets are enabled in the configuration.
     *
     * @method marketsEnabled
     * @memberof algolia.helpers
     * @returns {boolean} True if markets are enabled and mapping exists
     *
     * @example
     * if (algolia.helpers.marketsEnabled()) { ... }
     */
    marketsEnabled: function marketsEnabled() {
      return algolia.config.markets_indexing_enabled && algolia.config.markets_mapping?.length ? true : false;
    },

    /**
     * Gets the currently active currency code from Shopify.
     *
     * @method activeCurrencyCode
     * @memberof algolia.helpers
     * @returns {string} Lowercase currency code (e.g., 'usd', 'eur')
     *
     * @example
     * const currency = algolia.helpers.activeCurrencyCode();
     */
    activeCurrencyCode: function activeCurrencyCode() {
      return Shopify.currency.active.toLowerCase();
    },

    /**
     * Formats a number with thousand separators.
     *
     * @method formatNumber
     * @memberof algolia.helpers
     * @param {number|string} val - The number to format
     * @returns {string} Formatted number with locale separators
     *
     * @example
     * algolia.helpers.formatNumber(1234567); // Returns "1,234,567"
     */
    formatNumber: function formatNumber(val) {
      return Number(val).toLocaleString();
    },

    /**
     * Applies market-based pricing data to a search hit.
     * Modifies the hit object in-place with market-specific prices if applicable.
     *
     * @method localizeHitData
     * @memberof algolia.helpers
     * @param {Object} hit - Algolia search hit object
     * @returns {Object} Modified hit object with market pricing applied
     *
     * @example
     * const localizedHit = algolia.helpers.localizeHitData(hit);
     */
    localizeHitData: hit => {
      // Set default currency to store currency if markets enabled
      if (algolia.helpers.marketsEnabled()) {
        hit.currency = algolia.config.store_currency.toLowerCase();
      }
      
      // Skip localization if markets disabled or currency matches store currency
      if (!algolia.helpers.marketsEnabled() || algolia.helpers.activeCurrencyCode() === algolia.config.store_currency.toLowerCase()) {
        return hit;
      }

      // Build market pricing key based on active currency
      const currencyKey = `market_pricing_${algolia.helpers.activeCurrencyCode()}`;

      // Apply market-specific prices if available
      if (currencyKey in hit) {
        hit.price = hit[currencyKey]?.price || hit.price;
        hit.compare_at_price = hit[currencyKey]?.compare_at_price || hit.compare_at_price;
        hit.variants_min_price = hit[currencyKey]?.variants_min_price || hit.variants_min_price;
        hit.variants_max_price = hit[currencyKey]?.variants_max_price || hit.variants_max_price;
        hit.variants_compare_at_price_min = hit[currencyKey]?.variants_compare_at_price_min || hit.variants_compare_at_price_min;
        hit.variants_compare_at_price_max = hit[currencyKey]?.variants_compare_at_price_max || hit.variants_compare_at_price_max;
        hit.price_ratio = hit[currencyKey]?.price_ratio || hit.price_ratio;
        hit.currency = currencyKey.split('_').pop();
      }

      return hit;
    },

    /**
     * Displays the appropriate price for a product hit.
     * Handles custom prices, min/max ranges, and currency formatting.
     *
     * @method displayPrice
     * @memberof algolia.helpers
     * @param {Object} item - Product hit object
     * @param {boolean} distinct - Whether distinct mode is enabled (shows price range)
     * @returns {string} Formatted price string
     *
     * @example
     * algolia.helpers.displayPrice(hit, true); // Returns "$10.00 - $25.00"
     */
    displayPrice: function displayPrice(item, distinct) {
      // Return custom price if set
      if(item.hasOwnProperty('customPrice')) {
        return formatPrice(item.customPrice, item.currency);
      }
      // Show price range for distinct (variant) products
      if (distinct) {
        var min = item.variants_min_price;
        var max = item.variants_max_price;
        var currency = item.currency || false;
        if (min && max && min !== max) {
          return  formatPrice(min, currency) + ' - ' + formatPrice(max, currency);
        }
      }
      // Default to single price
      return formatPrice(item.price, item.currency);
    },

    /**
     * Displays strikethrough price if compare_at_price is higher than current price.
     *
     * @method displayStrikedPrice
     * @memberof algolia.helpers
     * @param {number|string} price - Current price
     * @param {number|string} compare_at_price - Original/higher price
     * @param {string} currency - Currency code
     * @returns {string|undefined} Formatted strikethrough price or undefined
     *
     * @example
     * algolia.helpers.displayStrikedPrice(10, 20, 'USD'); // Returns "$20.00"
     */
    displayStrikedPrice: function displayStrikedPrice(price, compare_at_price, currency) {
      const comparing =
        Number(compare_at_price) && Number(compare_at_price) > Number(price);
      if (comparing) { return formatPrice(compare_at_price, currency) }
    },

    /**
     * Calculates and displays discount percentage.
     *
     * @method displayDiscount
     * @memberof algolia.helpers
     * @param {number|string} price - Current price
     * @param {number|string} compare_at_price - Original price
     * @param {number} price_ratio - Price ratio (current/original)
     * @returns {string|undefined} Discount percentage string or undefined
     *
     * @example
     * algolia.helpers.displayDiscount(80, 100, 0.8); // Returns "- 20 %"
     */
    displayDiscount: function displayDiscount(price, compare_at_price, price_ratio) {
      const comparing = Number(compare_at_price) && Number(compare_at_price) > Number(price);
      const discount_ratio = 1.0 - price_ratio;
      if (comparing) { return `- ${Math.floor(discount_ratio * 100)} %`}
    },

    /**
     * Generates the product page URL with optional variant ID.
     *
     * @method instantsearchLink
     * @memberof algolia.helpers
     * @param {Object} hit - Product hit object
     * @returns {string} Product URL with optional variant parameter
     *
     * @example
     * algolia.helpers.instantsearchLink(hit);
     * // Returns "/products/product-handle?variant=123456789"
     */
    instantsearchLink: function instantsearchLink(hit) {
      const addVariantId = !hit._distinct && hit.objectID !== hit.id;
      return (
        Shopify.routes.root +
        'products/' +
        hit.handle +
        (addVariantId ? '?variant=' + hit.objectID : '')
      );
    },

    /**
     * Builds full product title with variant information.
     *
     * @method fullTitle
     * @memberof algolia.helpers
     * @param {string} title - Base product title
     * @param {boolean} distinct - Whether distinct mode is enabled
     * @param {string} variant_title - Variant title to append
     * @returns {string} Full title with variant if applicable
     *
     * @example
     * algolia.helpers.fullTitle('Shirt', false, 'Large Blue');
     * // Returns "Shirt (Large Blue)"
     */
    fullTitle: function fullTitle(title, distinct, variant_title) {
      var res = title;
      if (
        !distinct &&
        variant_title &&
        variant_title !== 'Default Title' &&
        variant_title !== 'Default'
      ) {
        res += ' (' + variant_title + ')';
      }

      return escapeHtml(res);
    },

    /**
     * Generates variant title addition for display.
     *
     * @method variantTitleAddition
     * @memberof algolia.helpers
     * @param {Object} item - Product hit object
     * @param {boolean} distinct - Whether distinct mode is enabled
     * @returns {string} Variant title string with formatting
     *
     * @example
     * algolia.helpers.variantTitleAddition(hit, false);
     * // Returns " (Large Blue)"
     */
    variantTitleAddition: function variantTitleAddition(item, distinct) {
      var res = ' ';
      if (
        !distinct &&
        item.variant_title &&
        item.variant_title !== 'Default Title' &&
        item.variant_title !== 'Default'
      ) {
        res += `( ${item.variant_title} )`;
      }
      return res;
    },

    /**
     * Converts hex color code to RGB format.
     *
     * @method hexToRGB
     * @memberof algolia.helpers
     * @param {string} hex - Hex color code (with or without #)
     * @returns {string} Comma-separated RGB values
     *
     * @example
     * algolia.helpers.hexToRGB('#FF0000'); // Returns "255,0,0"
     */
    hexToRGB: function hexToRGB(hex){
      if (!hex) return ``;
      // Expand shorthand hex (#F00) to full form (#FF0000)
      if(hex.length== 4){
        hex = hex.substring(1).replace(/./g, '$&$&')
      }
      var m = hex.match(/^#?([\da-f]{2})([\da-f]{2})([\da-f]{2})$/i);
      return `${parseInt(m[1], 16)},${parseInt(m[2], 16)},${parseInt(m[3], 16)}`;
    },

    /**
     * Generates sized image URL for product images.
     *
     * @method sizedImage
     * @memberof algolia.helpers
     * @param {string} sizeInput - Size name (pico, icon, thumb, small, compact, medium, large, grande, original)
     * @param {Object} item - Product hit object
     * @param {boolean} _distinct - Whether to use product_image vs image
     * @returns {string} Image URL with size suffix or placeholder
     *
     * @example
     * algolia.helpers.sizedImage('thumb', hit, false);
     * // Returns "https://cdn.shopify.com/.../product-thumb.jpg"
     */
    sizedImage: function sizedImage(sizeInput, item, _distinct) {
      var image = _distinct ? item.product_image : item.image;
      if (!image) {
        return 'http://cdn.shopify.com/s/images/admin/no-image-compact.gif';
      }
      var size = sizeInput.replace(/^\s+|\s+$/g, ''); // Render and trim
      if (size === 'original') {
        return image;
      }
      return image.replace(/\/(.*)\.(\w{2,4})/g, '/$1_' + size + '.$2');
    },

    /**
     * Memoized index suffix getter for market-based searches.
     * Caches the result to avoid repeated API calls and localStorage lookups.
     *
     * @property {string} index_suffix - Cached index suffix (e.g., '_us_en')
     * @memberof algolia.helpers
     *
     * @example
     * const suffix = await algolia.helpers.index_suffix;
     */
    index_suffix: (function () {
      // LocalStorage key for caching market information
      const ALGOLIA_MARKET_KEY = 'algolia_market';
      let cache_suffix = '';
      let isMemoized = false;

      /**
       * Gets the Algolia index suffix based on current market.
       * Checks localStorage cache first, then queries Algolia if needed.
       *
       * @async
       * @method memorizedIndexSuffix
       * @returns {Promise<string>} Index suffix string (e.g., '_us_en' or '')
       */
      return async function memorizedIndexSuffix() {
        if (isMemoized) return cache_suffix;

        // Check if markets are enabled and configured
        if (algolia.config.markets_indexing_enabled && algolia.config.markets_mapping?.length) {
          // Check if market has changed, if changed, update market
          let shouldCheckMarketIndex = true;
          try {
            // Try to get cached market from localStorage
            const marketStorage = JSON.parse(localStorage.getItem(ALGOLIA_MARKET_KEY) || '{}');
            
            // Use cached suffix if market matches
            if (
              marketStorage.id == algolia.shopify.market.id && 
              marketStorage.locale == algolia.shopify.market.language.shop_locale.locale
            ) {
              cache_suffix = marketStorage.suffix ? `_${marketStorage.suffix}` : '';
              shouldCheckMarketIndex = false;
              isMemoized = true;
            } 
          } catch (e) {
            // Clear corrupted cache
            localStorage.removeItem(ALGOLIA_MARKET_KEY);
            cache_suffix = '';
            isMemoized =true;
          }
          
          // Query Algolia to find correct market suffix if needed
          if(shouldCheckMarketIndex) {
            try {
              // key = {market_id}_{market_language}
              const key = `${algolia.shopify.market.id}_${algolia.shopify.market.language.shop_locale.locale}`;
              const market = algolia.config.markets_mapping.find(market => market.key === key);
              if(market) {
                const indexSuffix = market.suffix ? `_${market.suffix}` : '';

                // indexName = {prefix}_{type}_{market_title}_{language_code}
                const indexName = `${algolia.config.index_prefix}products${indexSuffix}`;

                await algolia.searchClient.searchSingleIndex({
                  indexName: indexName,
                  searchParams: {
                    page: 1,
                    hitsPerPage: 1,
                    analytics: false,
                  },
                });
                cache_suffix = indexSuffix;
                localStorage.setItem(ALGOLIA_MARKET_KEY, JSON.stringify(market));
                isMemoized = true;
              }
            } catch (e) {
              // Index doesn't exist or error - clear cache
              localStorage.removeItem(ALGOLIA_MARKET_KEY);
              cache_suffix = '';
              isMemoized = true;
            }
          }
          return cache_suffix;
        }
        else {
          // Markets not enabled - return empty suffix
          return cache_suffix;
        }
      }
    })(),

    /**
     * Updates Algolia index settings with custom configuration.
     *
     * @method updateCustomSettings
     * @memberof algolia.helpers
     * @async
     * @param {Object} [settings={}] - Index settings object to apply
     * @param {string} [indexName=`${algolia.config.index_prefix}products`] - Target index name
     * @returns {Promise<Object>} Algolia API response
     *
     * @example
     * await algolia.helpers.updateCustomSettings({ hitsPerPage: 24 });
     */
    updateCustomSettings: async function updateCustomSettings(settings={}, indexName=`${algolia.config.index_prefix}products`) {
      const response = await algolia.searchClient.setSettings({
        indexName: indexName,
        indexSettings: settings,
        forwardToReplicas: true,
      });
    }
  };

  // Initialize index suffix as a promise
  algolia.config.index_suffix = algolia.helpers.index_suffix();

  // Generate image helper functions for each standard Shopify image size
  [
    'pico',
    'icon',
    'thumb',
    'small',
    'compact',
    'medium',
    'large',
    'grande',
    'original'
  ].forEach(function(size) {
    algolia.helpers[size + 'Image'] = function(item) {
        var image = item.distinct ? item.product_image : item.image;

        if (!image) {
          return 'http://cdn.shopify.com/s/images/admin/no-image-compact.gif';
        }

        if (size === 'original') {
          return image;
        }

        return image.replace(/\/(.*)\.(\w{2,4})/g, '/$1_' + size + '.$2');
    };
  });
})(window.algoliaShopify);
